import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RankingBarChart } from "../../charts/ranking-bar-chart";
import type { ChartConfig, ChartTooltipFormatter } from "../../ui/chart";
import { AchievementAdoptionItem } from "../../../types/analytics";

export function AchievementsAdoptionList({
    data,
}: {
    data: AchievementAdoptionItem[];
}) {
    const { t } = useTranslation("gamification");

    const rows = useMemo(
        () =>
            [...(data ?? [])]
                .sort((a, b) => b.percentage - a.percentage)
                .map((a) => ({
                    label: a.name ? t(`achievements.items.${a.name}.name`, a.name) : t("adoption.noName"),
                    value: a.percentage,
                    unlocked: a.unlocked,
                })),
        [data, t],
    );

    const config = useMemo<ChartConfig>(
        () => ({
            value: {
                label: t("adoption.tooltipLabel"),
                color: "hsl(var(--chart-1))",
            },
        }),
        [t],
    );

    const tooltipFormatter = useCallback<ChartTooltipFormatter>(
        (value, name, item) => {
            const unlockedVal = item.payload?.unlocked ?? 0;
            return `${value}% (${t("adoption.unlockedCount", { count: unlockedVal })})`;
        },
        [t],
    );

    if (rows.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {t("adoption.empty")}
            </p>
        );
    }

    return (
        <div className="max-h-[420px] overflow-y-auto pr-1">
            <div ref={(el) => { if (el) el.style.height = `${Math.max(rows.length * 34, 120)}px`; }}>
                <RankingBarChart
                    data={rows}
                    config={config}
                    unit="%"
                    yWidth={170}
                    tooltipFormatter={tooltipFormatter}
                />
            </div>
        </div>
    );
}
